import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { HERO_FALLBACK, HeroBanner } from "@/components/HeroBanner";
import { btnGhost, btnPrimary, Panel } from "./ui";

export const HeroAdmin = ({ s, setS, reload }) => {
  const [candidate, setCandidate] = useState(null);
  const generating = s.hero_proposals_status === "in_corso";
  useEffect(() => {
    if (!generating) return;
    const t = setInterval(() => api.get("/admin/settings").then((r) => setS((cur) => ({ ...cur, hero_proposals: r.data.hero_proposals, hero_proposals_status: r.data.hero_proposals_status, hero_proposals_error: r.data.hero_proposals_error }))), 4000);
    return () => clearInterval(t);
  }, [generating, setS]);

  const apply = async (url) => {
    try { const { data } = await api.put("/admin/hero/image", { url }); setS((cur) => ({ ...cur, ...data })); setCandidate(null); reload(); toast.success("Immagine hero pubblicata"); } catch (e) { toast.error(apiError(e)); }
  };
  const restore = async () => {
    try { const { data } = await api.post("/admin/hero/restore"); setS((cur) => ({ ...cur, ...data })); reload(); toast.success("Immagine precedente ripristinata"); } catch (e) { toast.error(apiError(e)); }
  };
  const generate = async () => {
    try { await api.post("/admin/hero/proposals"); setS((cur) => ({ ...cur, hero_proposals_status: "in_corso" })); } catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { setCandidate((await api.post("/admin/uploads", fd)).data.url); } catch (e) { toast.error(apiError(e)); }
  };
  const shown = candidate || s.hero_image_url || HERO_FALLBACK;

  return (
    <Panel title="Immagine hero" testId="hero-admin">
      <div className="flex flex-wrap gap-2 mb-4">
        <label data-testid="hero-upload-button" className={`${btnGhost} cursor-pointer`}><Upload className="w-4 h-4" /> Carica immagine
          <input data-testid="hero-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
        <button data-testid="hero-generate-button" disabled={generating} onClick={generate} className={btnGhost}>
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Generazione in corso…" : "Genera 3 proposte"}
        </button>
        <button data-testid="hero-restore-button" disabled={!s.hero_history?.length} onClick={restore} className={btnGhost}><RotateCcw className="w-4 h-4" /> Ripristina precedente</button>
      </div>
      {s.hero_proposals_error && <p className="text-xs text-[#8a2f22] mb-3" data-testid="hero-proposals-error">{s.hero_proposals_error}</p>}
      {s.hero_proposals?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-5" data-testid="hero-proposals">
          {s.hero_proposals.map((p, i) => (
            <button key={p.id} data-testid={`hero-proposal-${i}`} onClick={() => setCandidate(p.url)}
              className={`text-left border ${candidate === p.url ? "border-[#39251D] ring-1 ring-[#39251D]" : "border-[#D5B98C]/60"} ${s.hero_image_url === p.url ? "opacity-60" : ""}`}>
              <img src={p.url} alt={p.title} className="w-full aspect-[16/9] object-cover" />
              <span className="block p-1.5 text-[10px] uppercase tracking-widest text-[#956842]">{i + 1}. {p.title}{s.hero_image_url === p.url ? " · in uso" : ""}</span>
            </button>
          ))}
        </div>
      )}
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#956842] mb-2">{candidate ? "Anteprima (non ancora pubblicata)" : "Anteprima attuale"} · desktop</div>
      <div className="border border-[#D5B98C]/60 overflow-hidden max-w-full" style={{ width: 640, height: 344 }}>
        <div style={{ width: 1280, transform: "scale(0.5)", transformOrigin: "top left" }}><HeroBanner s={s} image={shown} mode="desktop" /></div>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#956842] mb-2">Smartphone</div>
          <div className="border-[6px] border-[#242321] rounded-[18px] overflow-hidden" style={{ width: 207, height: 375 }}>
            <div style={{ width: 390, transform: "scale(0.5)", transformOrigin: "top left" }}><HeroBanner s={s} image={shown} mode="mobile" /></div>
          </div>
        </div>
        {candidate && <button data-testid="hero-publish-button" onClick={() => apply(candidate)} className={btnPrimary}>Pubblica questa immagine</button>}
        {candidate && <button data-testid="hero-cancel-button" onClick={() => setCandidate(null)} className={btnGhost}>Annulla</button>}
      </div>
      <p className="mt-3 text-xs text-[#242321]/55">Titolo, sottotitolo e pulsante si modificano nel pannello Homepage; l'anteprima si aggiorna mentre scrivi. L'immagine precedente resta disponibile per il ripristino.</p>
    </Panel>
  );
};
